import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SiteLayout } from "@/components/site/SiteLayout";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import { getSettings } from "@/lib/api";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Companies from "@/pages/Companies";
import CompanyDetail from "@/pages/CompanyDetail";
import Leadership from "@/pages/Leadership";
import Governance from "@/pages/Governance";
import Careers from "@/pages/Careers";
import News from "@/pages/News";
import NewsDetail from "@/pages/NewsDetail";
import Contact from "@/pages/Contact";
import Policy from "@/pages/Policy";
import NotFound from "@/pages/NotFound";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminFounder from "@/pages/admin/AdminFounder";
import AdminNews from "@/pages/admin/AdminNews";
import AdminNewsEditor from "@/pages/admin/AdminNewsEditor";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminMessages from "@/pages/admin/AdminMessages";

const withLayout = (Page) => (
  <SiteLayout>
    <Page />
  </SiteLayout>
);

// Leadership is opt-in via the CMS. When disabled, direct access returns a
// proper 404 (the content route is not exposed and is excluded from the sitemap).
function LeadershipGate() {
  const { data: settings, isLoading } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  if (isLoading) return null;
  if (!settings?.leadershipEnabled) return <NotFound />;
  return <Leadership />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/" element={withLayout(Home)} />
            <Route path="/about" element={withLayout(About)} />
            <Route path="/companies" element={withLayout(Companies)} />
            <Route path="/companies/:slug" element={withLayout(CompanyDetail)} />
            <Route path="/leadership" element={withLayout(LeadershipGate)} />
            <Route path="/governance" element={withLayout(Governance)} />
            <Route path="/careers" element={withLayout(Careers)} />
            <Route path="/news" element={withLayout(News)} />
            <Route path="/news/:slug" element={withLayout(NewsDetail)} />
            <Route path="/contact" element={withLayout(Contact)} />
            <Route path="/privacy" element={<SiteLayout><Policy kind="privacy" /></SiteLayout>} />
            <Route path="/terms" element={<SiteLayout><Policy kind="terms" /></SiteLayout>} />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/brands" element={<ProtectedRoute><AdminBrands /></ProtectedRoute>} />
            <Route path="/admin/media" element={<ProtectedRoute><AdminMedia /></ProtectedRoute>} />
            <Route path="/admin/leadership" element={<ProtectedRoute><AdminFounder /></ProtectedRoute>} />
            <Route path="/admin/news" element={<ProtectedRoute><AdminNews /></ProtectedRoute>} />
            <Route path="/admin/news/new" element={<ProtectedRoute><AdminNewsEditor /></ProtectedRoute>} />
            <Route path="/admin/news/edit/:id" element={<ProtectedRoute><AdminNewsEditor /></ProtectedRoute>} />
            <Route path="/admin/messages" element={<ProtectedRoute><AdminMessages /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />

            <Route path="*" element={withLayout(NotFound)} />
          </Routes>
          <Toaster position="top-right" closeButton theme="dark" />
        </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
